import { Button } from "@/components/ui/button";
import { ArrowRight, Users, Target, Award, Zap } from "lucide-react";
import { SiLinkedin } from "react-icons/si";
import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import { WebsiteHeader } from "@/components/layout/website-header";
import { WebsiteFooter } from "@/components/layout/website-footer";
import { SaaSGamaLogo } from "@/components/ui/saasgama-logo";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const aboutFaqs = [
  { q: "Why was SaaSGama created?", a: "SaaSGama was created to help businesses gain better visibility into their software ecosystem and make smarter technology decisions." },
  { q: "What is the mission of SaaSGama?", a: "SaaSGama's mission is to simplify SaaS management and help organizations maximize the value of their software investments." },
  { q: "Who does SaaSGama help?", a: "SaaSGama helps businesses of all sizes improve software visibility, optimize resources, and streamline software management." },
  { q: "What makes SaaSGama different?", a: "SaaSGama focuses on helping organizations gain clarity, control, and actionable insights across their software environment." },
  { q: "Why do businesses trust SaaSGama?", a: "Businesses trust SaaSGama for its commitment to simplifying software management and supporting informed decision-making." },
  { q: "What challenges inspired SaaSGama?", a: "SaaSGama was built to address the growing complexity of managing software applications, subscriptions, and business visibility." },
  { q: "What does SaaSGama believe about software management?", a: "SaaSGama believes businesses should have complete visibility and control over the tools that power their operations." },
  { q: "How does SaaSGama support business growth?", a: "SaaSGama helps organizations build a more efficient software ecosystem that supports scalability and long-term success." },
  { q: "What industries can benefit from SaaSGama?", a: "SaaSGama supports organizations across industries that want greater visibility into their software and technology investments." },
  { q: "What is SaaSGama's vision for the future?", a: "SaaSGama aims to help businesses simplify software management through smarter insights, greater visibility, and innovation." },
];

export default function AboutPage() {

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <Helmet>
        <title>About SaaSGama | SaaS Management & Cost Optimization</title>
        <meta name="description" content="Learn how SaaSGama simplifies SaaS Management with license management, SaaS cost optimization, and software spend management solutions for better control." />
        <meta name="keywords" content="SaaS Management, License Management, SaaS Cost Optimization, Software Spend Management" />
        <link rel="canonical" href="https://saasgama.com/about" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://saasgama.com/about" />
        <meta property="og:site_name" content="SaaSGama" />
        <meta property="og:title" content="About SaaSGama | SaaS Management & Cost Optimization" />
        <meta property="og:description" content="Learn how SaaSGama simplifies SaaS Management with license management, SaaS cost optimization, and software spend management solutions for better control." />
        <meta property="og:image" content="https://saasgama.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About SaaSGama | SaaS Management & Cost Optimization" />
        <meta name="twitter:description" content="Learn how SaaSGama simplifies SaaS Management with license management, SaaS cost optimization, and software spend management solutions for better control." />
        <meta name="twitter:image" content="https://saasgama.com/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            { "@type": "Organization", "@id": "https://saasgama.com/#organization", "name": "SaaSGama", "url": "https://saasgama.com", "logo": "https://saasgama.com/logo.png", "description": "SaaSGama helps organizations gain visibility into their software ecosystem, optimize resources, and make smarter technology decisions." },
            { "@type": "AboutPage", "@id": "https://saasgama.com/about#aboutpage", "url": "https://saasgama.com/about", "name": "About SaaSGama", "description": "Learn about SaaSGama's mission, vision, and commitment to helping businesses simplify software management and maximize software value.", "about": { "@id": "https://saasgama.com/#organization" } },
            { "@type": "FAQPage", "@id": "https://saasgama.com/about#faq", "mainEntity": aboutFaqs.map(faq => ({ "@type": "Question", "name": faq.q, "acceptedAnswer": { "@type": "Answer", "text": faq.a } })) }
          ]
        })}</script>
      </Helmet>

      <WebsiteHeader showBackButton />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-6">
              About{' '}
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                SaaSGama
              </span>
            </h1>
            <p className="text-base md:text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
              We're on a mission to help businesses take control of their software spending through intelligent license management and automated cost optimization.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-2xl md:text-4xl font-bold mb-6 text-gray-800">Our Mission</h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  In today's digital-first world, businesses rely on dozens of SaaS applications to operate efficiently. However, managing these licenses, tracking usage, and optimizing costs has become increasingly complex.
                </p>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  SAASGAMA was born from the frustration of watching companies overspend on unused software licenses while struggling to maintain visibility into their software portfolio.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  We believe every business deserves complete transparency and control over their software investments, which is why we've built the most comprehensive SaaS management platform available.
                </p>
              </div>
              <div className="relative">
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-8 rounded-2xl border border-blue-100">
                  <div className="grid grid-cols-2 gap-6">
                    <div className="text-center">
                      <div className="text-xl md:text-3xl font-bold text-blue-600 mb-2">500+</div>
                      <div className="text-gray-600">Companies Served</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl md:text-3xl font-bold text-green-600 mb-2">$50M+</div>
                      <div className="text-gray-600">Costs Saved</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl md:text-3xl font-bold text-purple-600 mb-2">10K+</div>
                      <div className="text-gray-600">Licenses Tracked</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl md:text-3xl font-bold text-orange-600 mb-2">98%</div>
                      <div className="text-gray-600">Customer Satisfaction</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-4xl font-bold mb-6 text-gray-800">Our Values</h2>
            <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto">
              These core principles guide everything we do and shape how we build products for our customers.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-base md:text-xl font-bold mb-3 text-gray-800">Transparency</h3>
              <p className="text-gray-600">
                Complete visibility into your software portfolio with no hidden costs or surprises.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-base md:text-xl font-bold mb-3 text-gray-800">Simplicity</h3>
              <p className="text-gray-600">
                Complex problems deserve simple solutions that anyone can understand and use.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-base md:text-xl font-bold mb-3 text-gray-800">Customer Focus</h3>
              <p className="text-gray-600">
                Every feature we build starts with understanding our customers' real challenges.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                <Award className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-base md:text-xl font-bold mb-3 text-gray-800">Excellence</h3>
              <p className="text-gray-600">
                We're committed to delivering exceptional quality in everything we create.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-4xl font-bold mb-6 text-gray-800">Meet Our Team</h2>
            <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto">
              A diverse group of passionate professionals dedicated to revolutionizing SaaS management.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-32 h-32 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white font-bold text-2xl">SA</span>
              </div>
              <h3 className="text-base md:text-xl font-bold mb-2 text-gray-800">Sarah Anderson</h3>
              <p className="text-gray-600 mb-3">CEO & Co-Founder</p>
              <p className="text-sm text-gray-500">
                Former McKinsey consultant with 10+ years of experience helping Fortune 500 companies optimize their technology spend.
              </p>
            </div>

            <div className="text-center">
              <div className="w-32 h-32 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white font-bold text-2xl">MC</span>
              </div>
              <h3 className="text-base md:text-xl font-bold mb-2 text-gray-800">Michael Chen</h3>
              <p className="text-gray-600 mb-3">CTO & Co-Founder</p>
              <p className="text-sm text-gray-500">
                Former Principal Engineer at Google with expertise in building scalable platforms and AI-driven optimization systems.
              </p>
            </div>

            <div className="text-center">
              <div className="w-32 h-32 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white font-bold text-2xl">EP</span>
              </div>
              <h3 className="text-base md:text-xl font-bold mb-2 text-gray-800">Emily Parker</h3>
              <p className="text-gray-600 mb-3">Head of Product</p>
              <p className="text-sm text-gray-500">
                Product leader with 8+ years at Slack and Dropbox, passionate about creating intuitive enterprise software experiences.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold mb-6 text-gray-800">Frequently Asked Questions</h2>
              <p className="text-base md:text-xl text-gray-600">
                Learn more about SaaSGama's mission and vision
              </p>
            </div>

            <Accordion type="single" collapsible className="w-full space-y-4">
              {aboutFaqs.map((faq, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border border-gray-200 rounded-lg px-6 bg-white shadow-sm hover:shadow-md transition-shadow">
                  <AccordionTrigger className="text-left text-lg font-semibold text-gray-800 hover:text-blue-600">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pt-2">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-5xl font-bold mb-6">
              Ready to join our mission?
            </h2>
            <p className="text-base md:text-xl mb-8 text-blue-100">
              Start optimizing your SaaS spending today and take control of your software investments
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold rounded-xl shadow-lg"
                asChild
              >
                <Link href="/register" className="flex items-center">
                  Get Started
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
              <Button 
                variant="outline"
                size="lg"
                className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-8 py-4 text-lg font-semibold rounded-xl transition-all"
                asChild
              >
                <Link href="/contact">Contact Sales</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <WebsiteFooter />
    </div>
  );
}
